import { Link } from 'react-router-dom';
import { DocsLayout } from '../components/DocsLayout';

export function DeleteAndRestorePage() {
  return (
    <DocsLayout
      title="Delete &amp; restore"
      updated="May 9, 2026"
      summary="What happens when you delete a project, how the 30-day grace window works, and how to ask Rynora support to restore something."
    >
      <h2>The two delete options</h2>

      <h3>Move to trash (the default)</h3>
      <p>
        When an Admin clicks <strong>Delete</strong> on a project, Rynora moves it to a per-organisation trash. The
        project disappears from your project list and from the global search, but every flow, run, screenshot, schedule,
        and variable inside it stays in our database for 30 days.
      </p>
      <p>
        You'll see the project in <strong>Projects → Recently Deleted</strong> with a countdown showing how many days
        until permanent deletion. To get it back, email <a href="mailto:support@rynora.app">support@rynora.app</a> with
        the project name; a Rynora support agent uses the platform admin console to restore it. Restores typically
        complete within one business day.
      </p>

      <h3>Permanently delete (the irreversible one)</h3>
      <p>
        Behind the &quot;Permanently delete&quot; toggle on the same dialog is a separate path that immediately wipes
        the project from our database. There is <strong>no undo</strong>, even by Rynora support, and the deletion
        cascades to every dependent record. We require you to type the project name to confirm.
      </p>
      <p>
        Use permanent delete only when you know you'll never need the data — the typical case is removing a test project
        that you created by accident. For everything else, prefer the regular &quot;Move to trash&quot; option.
      </p>

      <h2>The 30-day grace window</h2>
      <p>
        After 30 days a Rynora background worker runs <code>HARD_PURGE</code> on every project still in trash. The purge
        is logged, cascades to all child rows, and is permanent. We don't keep cold backups of your project data beyond
        this window.
      </p>
      <p>
        If you need a longer or shorter retention window for compliance reasons, contact{' '}
        <a href="mailto:support@rynora.app">support@rynora.app</a> — Enterprise plans can negotiate it.
      </p>

      <h2>What gets deleted vs what doesn't</h2>
      <table>
        <thead>
          <tr>
            <th>Resource</th>
            <th>Soft delete (30d)</th>
            <th>Permanent delete</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>The project record</td>
            <td>Hidden, kept</td>
            <td>Gone</td>
          </tr>
          <tr>
            <td>Flows, test cases, folders</td>
            <td>Hidden, kept</td>
            <td>Gone</td>
          </tr>
          <tr>
            <td>Run history, screenshots, traces</td>
            <td>Hidden, kept</td>
            <td>Gone</td>
          </tr>
          <tr>
            <td>Project variables (incl. secrets)</td>
            <td>Hidden, kept (encrypted)</td>
            <td>Gone</td>
          </tr>
          <tr>
            <td>Schedules</td>
            <td>Disabled, kept</td>
            <td>Gone</td>
          </tr>
          <tr>
            <td>Audit log entries about the project</td>
            <td>Kept (always)</td>
            <td>Kept (always)</td>
          </tr>
        </tbody>
      </table>
      <p>
        Audit log entries survive both kinds of delete — they're how you (and Rynora support) reconstruct what happened,
        and they're how we satisfy our own compliance obligations.
      </p>

      <h2>Who can delete</h2>
      <p>
        Only Admins and Owners can delete or permanently delete projects. See{' '}
        <Link to="/docs/user-roles">User roles</Link> for the full capability table. Users (the regular role) can
        request a project be deleted but can't perform the action themselves.
      </p>

      <h2>Asking us to restore</h2>
      <p>
        Email <a href="mailto:support@rynora.app">support@rynora.app</a> with:
      </p>
      <ul>
        <li>Your organisation name (or org ID, if you know it).</li>
        <li>The deleted project's name and approximate delete time.</li>
        <li>The email of the person who clicked Delete (we'll cross-check the audit log).</li>
      </ul>
      <p>
        The support agent uses Rynora's platform admin console, finds your org, opens the trash list, and clicks
        Restore. We log the action against the support ticket so you can audit it later.
      </p>

      <h2>Auditing the action yourself</h2>
      <p>
        From the Rynora API you can pull every <code>DELETE</code>, <code>HARD_PURGE</code>, and{' '}
        <code>UPDATE platform_admin_projects_restore</code> row that touched your org:
      </p>
      <pre className="docs-codeblock">{`GET /api/audit-log?action=DELETE,HARD_PURGE,UPDATE
    &entityType=project
    &since=2026-01-01

# Each row includes: actor_user_id, actor_role, target_org_id,
# bypass_reasons, entity_id, entity_name, meta (JSON), created_at.`}</pre>

      <h2>Common questions</h2>
      <p>
        <strong>Can a User (non-admin) recover a project they deleted?</strong> No — Users can't delete projects in the
        first place. Only Admins or Owners can.
      </p>
      <p>
        <strong>Can Rynora support restore after 30 days?</strong> No. The hard purge is irreversible by design — it's
        what lets us promise GDPR-style erasure.
      </p>
      <p>
        <strong>Will my variables and secrets come back when restored?</strong> Yes. Secrets stay encrypted at rest the
        entire time, including while the project is in trash. Restore is a metadata operation that flips one timestamp
        column.
      </p>
    </DocsLayout>
  );
}
